import { PeriodSelector } from "@/components/school-head/PeriodSelector";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePeriodRange } from "@/hooks/useSchoolHeadData";
import { format, parseISO } from "date-fns";

export default function SchoolHeadReferrals() {
  const { user } = useAuth();
  const { start, end } = usePeriodRange();

  const { data: referrals = [], isLoading } = useQuery({
    queryKey: ["sh-referrals", user?.schoolId, start, end],
    enabled: !!user?.schoolId && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("id,reason,status,date,hospital,student_id,students(surname,other_names,class)")
        .eq("school_id", user!.schoolId)
        .gte("date", start!)
        .lte("date", end!)
        .order("date", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const byStatus = referrals.reduce<Record<string, number>>((acc, r) => {
    const k = r.status || "UNKNOWN";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Referrals</h1>
          <p className="text-sm text-muted-foreground">Hospital referrals issued in this period.</p>
        </div>

        <PeriodSelector />

        <div className="flex flex-wrap gap-2">
          {Object.entries(byStatus).map(([k, v]) => (
            <Badge key={k} variant="outline" className="text-sm">
              {k}: {v}
            </Badge>
          ))}
          {Object.keys(byStatus).length === 0 && (
            <span className="text-sm text-muted-foreground">No referrals in this period.</span>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Class</th>
                    <th className="px-4 py-3">Reason (summary)</th>
                    <th className="px-4 py-3">Hospital</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>Loading…</td></tr>
                  ) : referrals.length === 0 ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>No referrals.</td></tr>
                  ) : (
                    referrals.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-4 py-2">
                          {r.date ? format(parseISO(r.date), "dd MMM yyyy") : "—"}
                        </td>
                        <td className="px-4 py-2 font-medium">
                          {r.students ? `${r.students.surname} ${r.students.other_names || ""}` : "—"}
                        </td>
                        <td className="px-4 py-2">{r.students?.class || "—"}</td>
                        <td className="px-4 py-2 max-w-[260px] truncate" title={r.reason || ""}>
                          {(r.reason || "—").split(/[.;]/)[0].slice(0, 60)}
                        </td>
                        <td className="px-4 py-2">{r.hospital || "—"}</td>
                        <td className="px-4 py-2">
                          <Badge variant="secondary">{r.status || "—"}</Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
